import React, { createContext, useContext, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

export interface OptimisticMove {
  id: string;                    // Temporary UUID
  driverId: number | null;        // Target driver row (null for Standard tier unassigned moves)
  startTime: Date;               // Appointment time (when driver arrives at customer)
  laneDurationSec: number;       // Forward lane duration (dealer -> customer)
  inverseLaneDurationSec?: number; // Inverse lane duration (customer -> dealer), for return trip
  status: 'creating' | 'offering' | 'completed' | 'failed';
  tier: 'standard' | 'lite';
  actualMoveId?: number;         // Set when real move is created
  driverName?: string;           // For "Offering to {name}..." message
  createdAt: Date;               // For timeout tracking
}

interface OptimisticMoveContextType {
  optimisticMoves: OptimisticMove[];
  addOptimisticMove: (move: Omit<OptimisticMove, 'id' | 'createdAt'>) => string;
  updateOptimisticMove: (id: string, updates: Partial<OptimisticMove>) => void;
  removeOptimisticMove: (id: string) => void;
  getOptimisticMoveById: (id: string) => OptimisticMove | undefined;
}

const OptimisticMoveContext = createContext<OptimisticMoveContextType | null>(null);

export const OptimisticMoveProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [optimisticMoves, setOptimisticMoves] = useState<OptimisticMove[]>([]);

  const addOptimisticMove = useCallback((move: Omit<OptimisticMove, 'id' | 'createdAt'>) => {
    const id = uuidv4();
    const newMove: OptimisticMove = {
      ...move,
      id,
      createdAt: new Date(),
    };
    setOptimisticMoves(prev => [...prev, newMove]);
    return id;
  }, []);

  const updateOptimisticMove = useCallback((id: string, updates: Partial<OptimisticMove>) => {
    setOptimisticMoves(prev =>
      prev.map(move => (move.id === id ? { ...move, ...updates } : move))
    );
  }, []);

  const removeOptimisticMove = useCallback((id: string) => {
    setOptimisticMoves(prev => prev.filter(move => move.id !== id));
  }, []);

  const getOptimisticMoveById = useCallback((id: string) => {
    return optimisticMoves.find(move => move.id === id);
  }, [optimisticMoves]);

  return (
    <OptimisticMoveContext.Provider
      value={{
        optimisticMoves,
        addOptimisticMove,
        updateOptimisticMove,
        removeOptimisticMove,
        getOptimisticMoveById,
      }}
    >
      {children}
    </OptimisticMoveContext.Provider>
  );
};

export const useOptimisticMoves = () => {
  const context = useContext(OptimisticMoveContext);
  if (!context) {
    throw new Error('useOptimisticMoves must be used within OptimisticMoveProvider');
  }
  return context;
};

export default OptimisticMoveContext;
